import React, { useState } from "react";
import { Button, Checkbox, Flex, Modal, Notification, TextField, toast } from "@telia/teddy";
import { useSelector } from "react-redux";
import {
    API_URL_ADD_EXISTING_SALES_AGENT_TO_DEALER,
    API_URL_CREATE_SALES_AGENT,
    API_URL_LOOKUP_SALES_AGENT_BY_PHONE,
    API_URL_UPDATE_SALES_AGENT,
} from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { RootState } from "../../../../../models";
import { getSalesAgent } from "../../state/selectors/selectors";
import { SalesAgent } from "../../types/models";

export type { SalesAgent };

interface Props {
    onClose: () => void;
    onCreated: (agent: SalesAgent) => void;
    onUpdated?: (agent: SalesAgent) => void;
    initialAgent?: SalesAgent;
}

type LookupStatus = "notFound" | "differentChain" | "sameChain" | "alreadyAtDealer" | "inactive";
type CreateMode = "lookup" | "create" | "attach";

interface LookupResponse {
    status: LookupStatus;
    proceedToCreate: boolean;
    agent?: SalesAgent;
}

const CreateSalesAgentModal: React.FC<Props> = ({ onClose, onCreated, onUpdated, initialAgent }) => {
    const isEditing = initialAgent !== undefined;
    const loggedInAgent = useSelector((state: RootState) => getSalesAgent(state));
    const showTcadField = !!loggedInAgent?.tcadUserId;
    const [mode, setMode] = useState<CreateMode>(isEditing ? "create" : "lookup");
    const [lookupPhone, setLookupPhone] = useState("");
    const [existingSalesAgentId, setExistingSalesAgentId] = useState<number | null>(null);
    const [firstName, setFirstName] = useState(initialAgent?.firstName ?? "");
    const [lastName, setLastName] = useState(initialAgent?.lastName ?? "");
    const [email, setEmail] = useState(initialAgent?.email ?? "");
    const [phone, setPhone] = useState(initialAgent?.telephoneNumber ?? "");
    const [isManager, setIsManager] = useState(initialAgent?.manager ?? false);
    const [tcadUserId, setTcadUserId] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lookupInfoMessage, setLookupInfoMessage] = useState<string | null>(null);
    const [success, setSuccess] = useState<{ password: string } | null>(null);

    const mapLookupError = (status: LookupStatus): string | null => {
        if (status === "alreadyAtDealer") return "Selger finnes allerede på denne forhandleren.";
        if (status === "inactive") return "Selger finnes i kjeden, men er inaktiv.";
        return null;
    };

    const getActiveHandler = (): (() => void) => {
        if (isEditing) return handleUpdate;
        if (mode === "attach") return handleAttach;
        return handleCreate;
    };

    const getActiveButtonLabel = (): string => {
        if (isEditing) return "Oppdater";
        if (mode === "attach") return "Legg til i butikk";
        return "Opprett";
    };

    const handleLookupByPhone = () => {
        setSubmitting(true);
        setError(null);
        setLookupInfoMessage(null);

        if (!lookupPhone.trim()) {
            setError("Mobilnummer er påkrevd");
            setSubmitting(false);
            return;
        }

        retailerFetch(API_URL_LOOKUP_SALES_AGENT_BY_PHONE, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phone: lookupPhone }),
        })
            .then((res) => {
                if (!res.ok)
                    return res.json().then((d) => {
                        throw new Error(d?.error ?? `Feil (${res.status})`);
                    });
                return res.json() as Promise<LookupResponse>;
            })
            .then((data) => {
                const mappedError = mapLookupError(data.status);
                if (mappedError) {
                    setError(mappedError);
                    setLookupInfoMessage(null);
                    return;
                }

                if (data.status === "sameChain" && data.agent) {
                    setMode("attach");
                    setLookupInfoMessage(
                        'Dette nummeret er allerede tilknyttet kjeden. Se over informasjon og trykk "Legg til i butikk" for å fortsette.',
                    );
                    setExistingSalesAgentId(data.agent.id);
                    setFirstName(data.agent.firstName ?? "");
                    setLastName(data.agent.lastName ?? "");
                    setEmail(data.agent.email ?? "");
                    setPhone(data.agent.telephoneNumber ?? lookupPhone);
                    setIsManager(data.agent.manager ?? false);
                    return;
                }

                setMode("create");
                setLookupInfoMessage(
                    'Ingen treff på telefonnummeret. Fyll inn informasjon og trykk "Opprett" for å fortsette.',
                );
                setPhone(lookupPhone);
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => setSubmitting(false));
    };

    const handleCreate = () => {
        setSubmitting(true);
        setError(null);
        if (isManager && !password.trim()) {
            setError("Passord er påkrevd");
            setSubmitting(false);
            return;
        }
        retailerFetch(API_URL_CREATE_SALES_AGENT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                firstName,
                lastName,
                email,
                phone,
                isManager,
                tcadUserId: tcadUserId || undefined,
                password: isManager ? password || undefined : undefined,
            }),
        })
            .then((res) => {
                if (!res.ok)
                    return res.json().then((d) => {
                        throw new Error(d?.error ?? `Feil (${res.status})`);
                    });
                return res.json() as Promise<{ success: boolean; salesAgentId: number; password: string }>;
            })
            .then((data) => {
                setSuccess({ password: data.password });
                toast.success("Selger opprettet", { position: "top-center" });
                onCreated({
                    id: data.salesAgentId,
                    firstName,
                    lastName,
                    email,
                    active: true,
                    manager: isManager,
                    telephoneNumber: phone,
                });
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => setSubmitting(false));
    };

    const handleAttach = () => {
        if (!existingSalesAgentId) {
            setError("Mangler selger-ID for eksisterende selger");
            return;
        }

        if (isManager && !password.trim()) {
            setError("Passord er påkrevd");
            setSubmitting(false);
            return;
        }

        setSubmitting(true);
        setError(null);

        retailerFetch(API_URL_ADD_EXISTING_SALES_AGENT_TO_DEALER, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                salesAgentId: existingSalesAgentId,
                firstName,
                lastName,
                email,
                isManager,
                password: isManager ? password || undefined : undefined,
            }),
        })
            .then((res) => {
                if (!res.ok)
                    return res.json().then((d) => {
                        throw new Error(d?.error ?? `Feil (${res.status})`);
                    });
                return res.json() as Promise<{ success: boolean; alreadyAtDealer?: boolean; salesAgent?: SalesAgent }>;
            })
            .then((data) => {
                const createdAgent: SalesAgent = data.salesAgent ?? {
                    id: existingSalesAgentId,
                    firstName,
                    lastName,
                    email,
                    active: true,
                    manager: isManager,
                    telephoneNumber: phone,
                };

                onCreated(createdAgent);
                toast.success("Selger lagt til i butikk", { position: "top-center" });
                onClose();
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => setSubmitting(false));
    };

    const handleUpdate = () => {
        if (!initialAgent) return;
        setSubmitting(true);
        setError(null);
        retailerFetch(API_URL_UPDATE_SALES_AGENT, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                salesAgentId: initialAgent.id,
                firstName,
                lastName,
                email,
                phone,
                isManager,
                tcadUserId: tcadUserId || undefined,
            }),
        })
            .then((res) => {
                if (!res.ok)
                    return res.json().then((d) => {
                        throw new Error(d?.error ?? `Feil (${res.status})`);
                    });
                return res.json() as Promise<{ success: boolean }>;
            })
            .then(() => {
                onUpdated?.({
                    ...initialAgent,
                    firstName,
                    lastName,
                    email,
                    manager: isManager,
                    telephoneNumber: phone,
                });
                toast.success("Selger endret", { position: "top-center" });
                onClose();
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal defaultOpen open>
            <Modal.Overlay>
                <Modal.Content style={{ width: "540px", maxWidth: "90vw", flexDirection: "column" }}>
                    <Modal.Title as="h3">{isEditing ? "Rediger selger" : "Opprett ny selger"}</Modal.Title>
                    <Modal.Close aria-label="lukk" slot="floating" onClick={onClose} />
                    <Modal.Group direction="column">
                        {error && (
                            <Notification type="tile" variant="error" mt="100" mb="200">
                                <Notification.Icon />
                                <Notification.Text>{error}</Notification.Text>
                            </Notification>
                        )}
                        {success ? (
                            <>
                                <Notification type="tile" variant="success" mt="100" mb="200">
                                    <Notification.Icon />
                                    <Notification.Text>
                                        Selger er opprettet. Midlertidig passord: <strong>{success.password}</strong>
                                    </Notification.Text>
                                </Notification>
                                <Button variant="primary" onClick={onClose} mt="300">
                                    Lukk
                                </Button>
                            </>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
                                {!isEditing && mode === "lookup" ? (
                                    <>
                                        <TextField>
                                            <TextField.Label>Mobilnummer</TextField.Label>
                                            <TextField.Input
                                                value={lookupPhone}
                                                onChange={(e) => setLookupPhone(e.target.value)}
                                                type="tel"
                                                data-di-mask
                                            />
                                        </TextField>
                                        <Flex gap="200" mt="300">
                                            <Button
                                                variant="primary"
                                                loading={submitting}
                                                disabled={submitting}
                                                onClick={handleLookupByPhone}
                                            >
                                                Søk
                                            </Button>
                                            <Button variant="secondary" onClick={onClose}>
                                                Avbryt
                                            </Button>
                                        </Flex>
                                    </>
                                ) : (
                                    <>
                                        {!isEditing && lookupInfoMessage && (
                                            <Notification type="tile" variant="information" mt="100" mb="200">
                                                <Notification.Icon />
                                                <Notification.Text>{lookupInfoMessage}</Notification.Text>
                                            </Notification>
                                        )}
                                        <TextField>
                                            <TextField.Label>Fornavn</TextField.Label>
                                            <TextField.Input
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                data-di-mask
                                            />
                                        </TextField>
                                        <TextField>
                                            <TextField.Label>Etternavn</TextField.Label>
                                            <TextField.Input
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                data-di-mask
                                            />
                                        </TextField>
                                        <TextField>
                                            <TextField.Label>Mobilnummer</TextField.Label>
                                            <TextField.Input
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                type="tel"
                                                data-di-mask
                                            />
                                        </TextField>
                                        <TextField>
                                            <TextField.Label>E-post</TextField.Label>
                                            <TextField.Input
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                type="email"
                                                data-di-mask
                                            />
                                        </TextField>
                                        {showTcadField && (
                                            <TextField>
                                                <TextField.Label>
                                                    TCAD bruker-ID {isEditing ? "" : "(valgfritt)"}
                                                </TextField.Label>
                                                <TextField.Input
                                                    value={tcadUserId}
                                                    onChange={(e) => setTcadUserId(e.target.value)}
                                                />
                                            </TextField>
                                        )}
                                        <Checkbox>
                                            <Checkbox.Trigger
                                                checked={isManager}
                                                onCheckedChange={(state) => setIsManager(!!state)}
                                            />
                                            <span>Butikksjef</span>
                                        </Checkbox>
                                        {!isEditing && isManager && (
                                            <TextField>
                                                <TextField.Label>Passord</TextField.Label>
                                                <TextField.Input
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    type="password"
                                                    autoComplete="new-password"
                                                />
                                            </TextField>
                                        )}
                                        <Flex gap="200" mt="300">
                                            <Button
                                                variant="primary"
                                                loading={submitting}
                                                disabled={submitting}
                                                onClick={getActiveHandler()}
                                            >
                                                {getActiveButtonLabel()}
                                            </Button>
                                            {!isEditing && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setMode("lookup");
                                                        setError(null);
                                                        setLookupInfoMessage(null);
                                                    }}
                                                >
                                                    Tilbake
                                                </Button>
                                            )}
                                            <Button variant="secondary" onClick={onClose}>
                                                Avbryt
                                            </Button>
                                        </Flex>
                                    </>
                                )}
                            </div>
                        )}
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default CreateSalesAgentModal;
